import React, { useState, useCallback, useMemo } from 'react';

export interface FieldOption {
  id: string;
  text: string;
  icon?: string;
  selected?: boolean;
}

export interface CategoryOption {
  text: string;
  children: FieldOption[];
}

export interface StatsFilters {
  fieldIds: string[];
  periodStart: string | null;
  periodEnd: string | null;
}

export interface StatsFiltersLabels {
  filterByActivity: string;
  dateRange: string;
  predefinedRanges: string;
  thisMonth: string;
  lastMonth: string;
  thisYear: string;
  clearAllFilters: string;
  selectActivities: string;
}

export interface StatsFiltersProps {
  filters: StatsFilters;
  categories: CategoryOption[];
  oldestEntryDate: string;
  defaultPeriodStart: string;
  defaultPeriodEnd: string;
  onChange: (filters: StatsFilters) => void;
  labels: StatsFiltersLabels;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export function StatsFilters({
  filters,
  categories,
  oldestEntryDate,
  defaultPeriodStart,
  defaultPeriodEnd,
  onChange,
  labels,
  isExpanded = false,
}: StatsFiltersProps) {
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  // Get all fields flattened
  const allFields = useMemo(() => {
    return categories.flatMap(cat => cat.children);
  }, [categories]);

  // Get selected fields
  const selectedFields = useMemo(() => {
    return allFields.filter(f => filters.fieldIds.includes(f.id));
  }, [allFields, filters.fieldIds]);

  // Check if period is default
  const isDefaultPeriod = filters.periodStart === defaultPeriodStart && filters.periodEnd === defaultPeriodEnd;

  // Check if any filters are applied
  const filtersApplied = filters.fieldIds.length > 0 || !isDefaultPeriod;

  // Predefined date ranges
  const today = new Date();
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
  const thisYearStart = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
  const todayStr = today.toISOString().split('T')[0];

  const handleFieldToggle = useCallback((fieldId: string) => {
    const newFieldIds = filters.fieldIds.includes(fieldId)
      ? filters.fieldIds.filter(id => id !== fieldId)
      : [...filters.fieldIds, fieldId];
    onChange({ ...filters, fieldIds: newFieldIds });
  }, [filters, onChange]);

  const handleRemoveField = useCallback((fieldId: string) => {
    onChange({
      ...filters,
      fieldIds: filters.fieldIds.filter(id => id !== fieldId),
    });
  }, [filters, onChange]);

  const handleDateChange = useCallback((startDate: string, endDate: string) => {
    onChange({
      ...filters,
      periodStart: startDate,
      periodEnd: endDate,
    });
  }, [filters, onChange]);

  const handleResetPeriod = useCallback(() => {
    onChange({
      ...filters,
      periodStart: defaultPeriodStart,
      periodEnd: defaultPeriodEnd,
    });
  }, [filters, onChange, defaultPeriodStart, defaultPeriodEnd]);

  const handleClearAll = useCallback(() => {
    onChange({
      fieldIds: [],
      periodStart: defaultPeriodStart,
      periodEnd: defaultPeriodEnd,
    });
  }, [onChange, defaultPeriodStart, defaultPeriodEnd]);

  const handlePredefinedRange = useCallback((start: string, end: string) => {
    onChange({
      ...filters,
      periodStart: start,
      periodEnd: end,
    });
  }, [filters, onChange]);

  const isPredefinedActive = (start: string, end: string) => {
    return filters.periodStart === start && filters.periodEnd === end;
  };

  return (
    <div className="stats-filters">
      {/* Filter form */}
      <div className={`filter-dropdown-container collapse ${isExpanded ? 'show' : ''}`} id="filter">
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body p-3">
            <div className="row g-3">
              {/* Activity filter */}
              <div className="col-12 col-md-6">
                <label className="form-label small text-muted mb-1">
                  <i className="bi bi-funnel me-1"></i>
                  {labels.filterByActivity}
                </label>
                <div className="position-relative">
                  <div
                    className="form-select d-flex flex-wrap gap-1 align-items-center cursor-pointer"
                    style={{ minHeight: '38px', cursor: 'pointer' }}
                    onClick={() => setIsSelectOpen(!isSelectOpen)}
                  >
                    {selectedFields.length === 0 ? (
                      <span className="text-muted">{labels.selectActivities}</span>
                    ) : (
                      selectedFields.map(field => (
                        <span key={field.id} className="badge bg-primary d-inline-flex align-items-center">
                          {field.icon && (
                            <i className={`icon-flex icon-${field.icon} me-1`} style={{ width: 14, height: 14 }} />
                          )}
                          {field.text}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-1"
                            style={{ fontSize: '0.6rem' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveField(field.id);
                            }}
                          />
                        </span>
                      ))
                    )}
                  </div>
                  {isSelectOpen && (
                    <div
                      className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                      style={{ zIndex: 1000, maxHeight: 300, overflow: 'auto' }}
                    >
                      {categories.map(category => (
                        <div key={category.text}>
                          <div className="px-3 py-1 bg-light fw-bold small">{category.text}</div>
                          {category.children.map(field => (
                            <div
                              key={field.id}
                              className="px-3 py-2 d-flex align-items-center cursor-pointer hover-bg-light"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleFieldToggle(field.id)}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input me-2"
                                checked={filters.fieldIds.includes(field.id)}
                                onChange={() => {}}
                              />
                              {field.icon && (
                                <i className={`icon-flex icon-${field.icon} me-2`} style={{ width: 20, height: 20 }} />
                              )}
                              {field.text}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Date range */}
              <div className="col-12 col-md-6">
                <label className="form-label small text-muted mb-1">
                  <i className="bi bi-calendar-range me-1"></i>
                  {labels.dateRange}
                </label>
                <div className="d-flex gap-2">
                  <input
                    type="date"
                    className="form-control"
                    value={filters.periodStart || ''}
                    min={oldestEntryDate}
                    max={filters.periodEnd || todayStr}
                    onChange={(e) => handleDateChange(e.target.value, filters.periodEnd || todayStr)}
                  />
                  <span className="align-self-center">-</span>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.periodEnd || ''}
                    min={filters.periodStart || oldestEntryDate}
                    max={todayStr}
                    onChange={(e) => handleDateChange(filters.periodStart || oldestEntryDate, e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Predefined ranges */}
            <div className="mt-3">
              <label className="form-label small text-muted mb-2">
                <i className="bi bi-clock-history me-1"></i>
                {labels.predefinedRanges}
              </label>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn btn-outline-primary ${isPredefinedActive(thisMonthStart, todayStr) ? 'active' : ''}`}
                  onClick={() => handlePredefinedRange(thisMonthStart, todayStr)}
                >
                  {labels.thisMonth}
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-primary ${isPredefinedActive(lastMonthStart, lastMonthEnd) ? 'active' : ''}`}
                  onClick={() => handlePredefinedRange(lastMonthStart, lastMonthEnd)}
                >
                  {labels.lastMonth}
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-primary ${isPredefinedActive(thisYearStart, todayStr) ? 'active' : ''}`}
                  onClick={() => handlePredefinedRange(thisYearStart, todayStr)}
                >
                  {labels.thisYear}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Applied filter badges */}
      {filtersApplied && (
        <div className="filter-applied-tags mb-3">
          {selectedFields.map(field => (
            <span key={field.id} className="badge bg-primary me-1 mb-1 filter-badge">
              {field.icon && (
                <i className={`icon-flex icon-${field.icon}`} style={{ width: 16, height: 16, verticalAlign: 'middle' }} />
              )}
              {field.text}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.5rem' }}
                onClick={() => handleRemoveField(field.id)}
              />
            </span>
          ))}

          {!isDefaultPeriod && (
            <span className="badge bg-info me-1 mb-1 filter-badge">
              <i className="bi bi-calendar-range me-1"></i>
              {filters.periodStart} - {filters.periodEnd}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.5rem' }}
                onClick={handleResetPeriod}
              />
            </span>
          )}

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ms-2"
            onClick={handleClearAll}
          >
            <i className="bi bi-x-circle"></i>
            {labels.clearAllFilters}
          </button>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {isSelectOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 999 }}
          onClick={() => setIsSelectOpen(false)}
        />
      )}
    </div>
  );
}

export default StatsFilters;
